import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { PaginationFooterTemplateComponent } from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { AbstractLayersTableComponent } from '@connect-services/client/components/project-settings/project-layers-settings/abstract-layers-table.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { LayersService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomTableComponent,
        PaginationFooterTemplateComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
    ],
    selector: 'app-interstandard-layers-table',
    styleUrl: '../project-layers-settings.component.scss',
    templateUrl: './interstandard-layers-table.component.html',
})
export class InterstandardLayersTableComponent extends AbstractLayersTableComponent implements OnInit {
    public readonly geomOnly = input<boolean>(true);
    public readonly searchValue = input<string>('');
    public updateInterstandardItems = output<number>();

    protected readonly interstandardDeposits: WritableSignal<{ [interstandardLayerId: string]: string }> = signal({});
    protected tableColumns: CustomColumn[] = [
        {
            key: 'name',
            label: 'layer',
        },
        {
            key: 'data',
            label: 'dataSource',
            sortable: false,
        },
    ];

    private readonly layersServiceSdk = inject(LayersService);

    public constructor() {
        super();

        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }

            this.searchValue();
            this.layerService.forceLayerListRefresh();

            untracked(() => this.loadLayers());
        });
    }

    public ngOnInit(): void {
        if (this.geomOnly()) {
            this.tableColumns.push(
                {
                    key: 'order',
                    label: 'order',
                },
                {
                    key: 'isVisible',
                    label: 'visibility',
                },
            );
        }
    }

    protected override async loadLayers(): Promise<void> {
        this.isLoading.set(true);

        const loadedLayers = await firstValueFrom(
            this.layersServiceSdk.layerControllerGet({
                geomFilter: this.geomOnly() ? 'ONLY_GEOM' : 'ONLY_NOGEOM',
                project: this.projectBloc.value()!.id,
                searchValue: this.searchValue(),
                types: [this.LayerTypeEnum.INTERSTANDARD],
                ...this.paginationParameters(),
            }),
        );

        this.paginationParameters.update((parameters) => {
            return {
                ...parameters,
                hasNextPage: loadedLayers.hasNextPage,
                hasPreviousPage: loadedLayers.hasPreviousPage,
                page: loadedLayers.page,
                totalItems: loadedLayers.totalItems,
                totalPages: loadedLayers.totalPages,
            };
        });

        this.layers.set(loadedLayers!.items);

        for (const layer of this.layers()) {
            if (layer.id in this.interstandardDeposits()) {
                continue;
            }

            const deposits = await firstValueFrom(
                this.layersServiceSdk.layerControllerGetDepositsByInterstandardLayerId({
                    id: layer.id,
                }),
            );
            const depositNamesList: string[] = [];

            for (const deposit of deposits) {
                depositNamesList.push(deposit.name);
            }

            this.interstandardDeposits.update((depositNames) => ({
                ...depositNames,
                [layer.id]: depositNamesList.join(', '),
            }));
        }

        this.updateInterstandardItems.emit(this.paginationParameters().totalItems ?? 0);

        this.isLoading.set(false);
    }
}
